/** Native disclosures remain usable before this optional enhancement loads. */
class HeaderInlineMenu extends HTMLElement {
  #controller;
  #backdrop;
  #dividerFrames = new Map();
  #closing = new Map();

  #cancelClose(details) {
    const pending = this.#closing.get(details);
    this.#closing.delete(details);
    clearTimeout(pending?.timeout);
    pending?.animation.cancel();
    details.removeAttribute('data-menu-closing');
  }

  #closeDisclosure(details, animate = false) {
    if (!details.open) { this.#cancelClose(details); return; }
    if (this.#closing.has(details) && animate) return;
    this.#cancelClose(details);
    const panel = [...details.children].find(child => child.classList.contains('header-inline-nav__submenu'));
    if (!animate || !this.closest('.header-hamburger') || !panel?.animate ||
        matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.open = false;
      return;
    }
    // Fade before hiding native details; do not animate their intrinsic height.
    // Rapid reopen/reparent cancels this one animation and its fallback together.
    details.setAttribute('data-menu-closing', '');
    let animation;
    try {
      animation = panel.animate([
        { opacity: 1, translate: '0 0' },
        { opacity: 0, translate: '0 -4px' },
      ], { duration: 180, easing: 'ease-out', fill: 'forwards' });
    } catch {
      this.#cancelClose(details);
      details.open = false;
      return;
    }
    const pending = { animation, timeout: null };
    const finish = () => {
      if (this.#closing.get(details) !== pending) return;
      // Remove the live animation before the browser hides the native panel.
      this.#cancelClose(details);
      details.open = false;
    };
    this.#closing.set(details, pending);
    pending.timeout = setTimeout(finish, 500);
    void animation.finished.then(finish, finish);
  }

  #replayDividers(details) {
    cancelAnimationFrame(this.#dividerFrames.get(details));
    this.#dividerFrames.delete(details);
    details.removeAttribute('data-dividers-visible');
    if (!details.open) return;
    // Let the reset paint before reapplying the animation, including a rapid
    // close/reopen that native details might otherwise combine in one frame.
    this.#dividerFrames.set(details, requestAnimationFrame(() => {
      this.#dividerFrames.set(details, requestAnimationFrame(() => {
        this.#dividerFrames.delete(details);
        if (details.open && this.isConnected) details.setAttribute('data-dividers-visible', '');
      }));
    }));
  }

  connectedCallback() {
    if (this.#controller) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const disclosures = [...this.querySelectorAll('.header-inline-nav__disclosure')];
    const hamburger = this.closest('.header-hamburger');
    const disclosureRoot = hamburger || this;
    this.setAttribute('data-divider-replay', '');
    if (disclosures.length || hamburger) {
      this.#backdrop = document.createElement('div');
      this.#backdrop.className = 'header-inline-menu-backdrop';
      this.#backdrop.setAttribute('aria-hidden', 'true');
      document.body.append(this.#backdrop);
    }
    let backdropDepth = -1;
    const syncBackdrop = () => {
      if (!this.#backdrop) return;
      let depth = hamburger?.open ? 1 : 0;
      for (const details of disclosureRoot.querySelectorAll('details[open]')) {
        let nesting = 1;
        let ancestor = details.parentElement.closest('details');
        let visible = true;
        while (ancestor && disclosureRoot.contains(ancestor)) {
          if (!ancestor.open) { visible = false; break; }
          nesting++;
          ancestor = ancestor.parentElement?.closest('details');
        }
        if (visible) depth = Math.max(depth, nesting);
      }
      if (depth === backdropDepth) return;
      backdropDepth = depth;
      this.#backdrop.style.setProperty('--menu-backdrop-opacity', String(Math.min(0.16 + (depth - 1) * 0.06, 0.28)));
      this.#backdrop.classList.toggle('is-open', depth > 0);
    };
    // Native toggle does not bubble; capture also covers nested disclosures
    // and the language picker mounted elsewhere inside the hamburger panel.
    disclosureRoot.addEventListener('toggle', syncBackdrop, { capture: true, signal });
    const close = (except, animate = false) => disclosures.forEach(details => {
      if (details !== except && !details.contains(except)) this.#closeDisclosure(details, animate);
    });

    for (const details of disclosures) {
      details.addEventListener('toggle', () => {
        // Native toggle events are queued. A previous opening event can arrive
        // after a newer tap has already started closing this disclosure.
        // Never let that stale event close the newly selected sibling.
        if (details.open && !this.#closing.has(details)) close(details, true);
        else if (!details.open) this.#cancelClose(details);
        if (!this.#closing.has(details)) this.#replayDividers(details);
        syncBackdrop();
      }, { signal });
      details.querySelector(':scope > summary')?.addEventListener('click', event => {
        if (!this.closest('.header-hamburger') || event.defaultPrevented) return;
        event.preventDefault();
        if (!details.open || this.#closing.has(details)) {
          this.#cancelClose(details);
          // Resolve the selection in this tap, before queued native toggles.
          close(details, true);
          details.open = true;
          this.#replayDividers(details);
        } else this.#closeDisclosure(details, true);
      }, { signal });
      details.parentElement.addEventListener('pointerenter', event => {
        if (this.closest('.header-hamburger') || details.hasAttribute('data-contact-panel')) return;
        if (event.pointerType !== 'mouse' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        close(details);
        details.open = true;
      }, { signal });
      details.parentElement.addEventListener('pointerleave', event => {
        if (this.closest('.header-hamburger') || details.hasAttribute('data-contact-panel')) return;
        if (event.pointerType === 'mouse' && !details.contains(document.activeElement)) details.open = false;
      }, { signal });
    }

    document.addEventListener('click', event => {
      if (!this.contains(event.target)) close();
    }, { signal });
    this.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const details = event.target.closest('details[open]');
      if (!details) return;
      event.preventDefault();
      this.#closeDisclosure(details, true);
      details.querySelector('summary')?.focus({ preventScroll: true });
    }, { signal });
    this.addEventListener('focusout', event => {
      // A native disclosure can temporarily lose focus while its layout changes.
      // Only an explicit focus destination outside the menu should dismiss it.
      if (!(event.relatedTarget instanceof Node)) return;
      if (!this.contains(event.relatedTarget)) close();
    }, { signal });
    this.addEventListener('click', event => {
      if (event.target.closest('.header-inline-nav__submenu a')) close();
    }, { signal });
    syncBackdrop();
    disclosures.filter(details => details.open).forEach(details => this.#replayDividers(details));
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#dividerFrames.forEach(frame => cancelAnimationFrame(frame));
    this.#dividerFrames.clear();
    for (const details of this.#closing.keys()) this.#cancelClose(details);
    this.removeAttribute('data-divider-replay');
    this.querySelectorAll('[data-dividers-visible]').forEach(details => details.removeAttribute('data-dividers-visible'));
    this.#backdrop?.remove();
    this.#backdrop = null;
  }
}

if (!customElements.get('header-inline-menu')) {
  customElements.define('header-inline-menu', HeaderInlineMenu);
}
